"use client";

import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";

import { Button } from "@/core/ui/button";

const STEPS = [
    {
        id: "objective",
        question: "What is the primary objective of your next initiative?",
        type: "textarea",
        placeholder: "e.g., We need to rebuild our core infrastructure...",
    },
    {
        id: "timeline",
        question: "What is your target timeline for this engagement?",
        type: "input",
        placeholder: "e.g., Q3, Within 2 months, Flexible...",
    },
    {
        id: "budget",
        question: "What is your allocated budget range?",
        type: "input",
        placeholder: "e.g., $15k - $30k, $50k+, TBD...",
    },
    {
        id: "identity",
        question: "Where should I send the evaluation?",
        type: "identity",
        placeholder: "Enter details below",
    }
];

export function GuidedProjectBrief() {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<Record<string, string>>({
        objective: "",
        timeline: "",
        budget: "",
        name: "",
        email: ""
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    // Auto-focus logic when step changes
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [currentStep]);

    const handleNext = () => {
        if (currentStep < STEPS.length - 1) {
            setCurrentStep(s => s + 1);
        } else {
            handleSubmit();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(s => s - 1);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            
            // Basic validation
            const stepId = STEPS[currentStep].id;
            if (stepId === 'identity' && (!formData.name || !formData.email)) {
                toast.error("Please provide both name and email.");
                return;
            }
            
            handleNext();
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.email || !formData.objective) {
            toast.error("Please ensure Name, Email, and Objective are filled.");
            return;
        }

        setIsLoading(true);

        const data = {
            name: formData.name,
            email: formData.email,
            message: `Objective:\n${formData.objective}\n\nTimeline:\n${formData.timeline || 'Not specified'}\n\nBudget:\n${formData.budget || 'Not specified'}`,
            projectType: 'Guided Brief Submission'
        };

        try {
            const response = await fetch('/api/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });

            if (!response.ok) throw new Error('Failed to send message');

            setIsSuccess(true);
            toast.success("Brief submitted successfully.");
        } catch (error) {
            toast.error("Something went wrong. Please try again.");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="h-full w-full relative group min-h-[400px] flex items-center justify-center">
                <div className="absolute -inset-0.5 bg-linear-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl opacity-75"></div>
                <div className="relative h-full w-full bg-card/90 backdrop-blur rounded-3xl border border-border/50 p-12 flex flex-col items-center justify-center text-center space-y-6 transition-colors duration-300">
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-10 h-10 text-green-500" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-bold text-foreground mb-2">Brief Received</h2>
                        <p className="text-muted-foreground text-lg max-w-md mx-auto">
                            Thank you for detailing your initiative. I&apos;ll review the scope and reach out shortly to discuss next steps.
                        </p>
                    </div>
                    <Button
                        onClick={() => {
                            setIsSuccess(false);
                            setCurrentStep(0);
                            setFormData({ objective: "", timeline: "", budget: "", name: "", email: "" });
                        }}
                        className="bg-secondary/50 hover:bg-secondary text-foreground rounded-full px-8 py-6 text-lg transition-colors border-none"
                    >
                        Submit Another Inquiry
                    </Button>
                </div>
            </div>
        );
    }

    const step = STEPS[currentStep];
    const progress = ((currentStep + 1) / STEPS.length) * 100;

    return (
        <div className="h-full w-full relative group min-h-[450px] flex flex-col">
            <div className="absolute -inset-0.5 bg-linear-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl opacity-50 group-hover:opacity-75 transition duration-1000"></div>
            
            <div className="relative flex-grow bg-card/90 backdrop-blur-xl rounded-3xl border border-border/50 overflow-hidden flex flex-col">
                
                {/* Progress Bar */}
                <div className="absolute top-0 left-0 h-1.5 bg-secondary w-full z-20">
                    <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        className="h-full bg-linear-to-r from-blue-500 to-purple-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                    />
                </div>

                <div className="p-8 md:p-12 flex flex-col flex-grow justify-center relative">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="w-full max-w-2xl mx-auto space-y-8"
                        >
                            <h2 className="text-3xl md:text-4xl font-medium text-foreground leading-tight">
                                {step.question}
                            </h2>

                            <div className="space-y-4">
                                {step.type === 'textarea' && (
                                    <textarea
                                        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                                        value={formData[step.id]}
                                        onChange={(e) => setFormData({ ...formData, [step.id]: e.target.value })}
                                        onKeyDown={handleKeyDown}
                                        placeholder={step.placeholder}
                                        className="w-full text-xl md:text-2xl bg-transparent border-none focus:ring-0 focus:outline-none text-foreground placeholder:text-muted-foreground/30 resize-none overflow-hidden h-32"
                                    />
                                )}
                                
                                {step.type === 'input' && (
                                    <input
                                        type="text"
                                        ref={inputRef as React.RefObject<HTMLInputElement>}
                                        value={formData[step.id]}
                                        onChange={(e) => setFormData({ ...formData, [step.id]: e.target.value })}
                                        onKeyDown={handleKeyDown}
                                        placeholder={step.placeholder}
                                        className="w-full text-xl md:text-2xl bg-transparent border-b border-border/50 focus:border-purple-500/50 pb-2 focus:outline-none text-foreground placeholder:text-muted-foreground/30 transition-colors"
                                    />
                                )}

                                {step.type === 'identity' && (
                                    <div className="space-y-6">
                                        <input
                                            type="text"
                                            ref={inputRef as React.RefObject<HTMLInputElement>}
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            onKeyDown={handleKeyDown}
                                            placeholder="Your full name"
                                            className="w-full text-xl md:text-2xl bg-transparent border-b border-border/50 focus:border-purple-500/50 pb-2 focus:outline-none text-foreground placeholder:text-muted-foreground/30 transition-colors"
                                        />
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            onKeyDown={handleKeyDown}
                                            placeholder="your@email.com"
                                            className="w-full text-xl md:text-2xl bg-transparent border-b border-border/50 focus:border-purple-500/50 pb-2 focus:outline-none text-foreground placeholder:text-muted-foreground/30 transition-colors"
                                        />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Navigation Controls */}
                    <div className="absolute bottom-6 left-8 right-8 flex justify-between items-center z-20">
                        <div className="flex items-center gap-4 text-muted-foreground">
                            {currentStep > 0 && (
                                <button 
                                    onClick={handlePrev}
                                    className="p-2 hover:bg-secondary/50 rounded-full transition-colors flex items-center gap-2 group cursor-pointer"
                                    title="Previous step"
                                >
                                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                                </button>
                            )}
                            <span className="text-sm font-mono tracking-widest uppercase ml-2">
                                {currentStep + 1} / {STEPS.length}
                            </span>
                        </div>
                        
                        <Button
                            onClick={handleNext}
                            disabled={isLoading}
                            className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-full font-medium transition-all group/next flex items-center gap-2 shadow-lg hover:shadow-primary/25 border-none"
                        >
                            {isLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : currentStep === STEPS.length - 1 ? (
                                <span>Complete Request</span>
                            ) : (
                                <>
                                    <span>Press Enter</span>
                                    <ArrowRight className="w-4 h-4 ml-1 opacity-70 group-hover/next:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
